// deliberationStreaming.ts

import { supabase } from '../lib/supabase';

const API_BASE_URL = import.meta.env.VITE_RAG_API_URL || 'http://localhost:8000';

export interface SSEEvent {
    type: 'stage_started' | 'stage_completed' | 'error' | 'done' | 'degraded';
    stage?: string;
    run_id: string;
    timestamp: string;
    payload: Record<string, unknown>;
}

export async function* streamDeliberation(
    question: string,
    context: string = '',
    signal?: AbortSignal
): AsyncGenerator<SSEEvent, void, unknown> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const response = await fetch(`${API_BASE_URL}/deliberate/stream`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ question, context }),
        signal,
    });

    if (response.status === 429) {
        const errorData = await response.json().catch(() => ({}));
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const err: any = new Error(errorData.detail?.error || 'DELIBERATION_IN_PROGRESS');
        err.remaining = errorData.detail?.remaining;
        err.active_run_id = errorData.detail?.active_run_id;
        throw err;
    }

    if (!response.ok) {
        throw new Error(`Failed to start deliberation: ${response.statusText}`);
    }
    if (!response.body) {
        throw new Error('Response body is null');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    // terminal = done OR error
    let terminalReceived = false;

    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
                if (!line.startsWith('data: ')) continue;

                const data = line.slice(6);
                try {
                    const event: SSEEvent = JSON.parse(data);

                    if (event.type === 'done' || event.type === 'error') {
                        terminalReceived = true;
                    }

                    yield event;
                } catch (e) {
                    console.error('Failed to parse SSE event:', data, e);
                }
            }
        }
    } finally {
        reader.releaseLock();
    }

    // Se o stream terminou sem terminal, aí sim é interrompido.
    if (!terminalReceived) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const err: any = new Error('STREAM_INTERRUPTED');
        err.code = 'STREAM_INTERRUPTED';
        throw err;
    }
}
